<template>
	<template v-if="store.isLoading">
		<Loading />
		<section class="h-[500px]" />
	</template>

	<ClientOnly v-else-if="!store.isLoading">
		<SearchedPosts :posts="posts" />

		<div
			ref="loadMoreAnchor"
			class="blog-loading-container"
		>
			<div class="post-loading">
				<img
					v-if="showLoad"
					src="/images/loading.svg"
					class="loading animation"
				/>
			</div>
		</div>
	</ClientOnly>
</template>

<script setup>
import axios from "axios";

const { hostname, env } = useRuntimeConfig().public;

const loadMoreAnchor = ref();

const showLoad = ref(false);
const loadMore = ref(false);
const posts = ref([]);

store.toggleLoading();

const pageNumber = ref(1);
const totalPages = ref(1);
const $route = useRoute();
const pageTitle = ref($route.query.q);

const loadPost = async (query) => {
	try {
		const { data, headers } = await axios.get(
			`${hostname}wp-json/wp/v2/search?per_page=20&page=${pageNumber.value}&search=${query}&_embed`
		);

		const totalPages = Number(headers["x-wp-totalpages"]);

		return { posts: data, totalPages };
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("ERROR   LOADING MORE POSTS");
	}
};

const isElementInViewport = (element) => {
	if (!element) {
		return false;
	}

	const rect = element.getBoundingClientRect();
	return rect.bottom > 0 && rect.top < window.innerHeight;
};

const handleVisibilityChange = async () => {
	const visibilityCheck = isElementInViewport(loadMoreAnchor.value);

	if (
		visibilityCheck &&
		loadMore.value === true &&
		pageNumber.value < totalPages.value
	) {
		pageNumber.value++;
		loadMore.value = false;

		const response = await loadPost($route.query.q);

		if (response.posts.length) {
			posts.value = posts.value.concat(response.posts);

			if (pageNumber.value < totalPages.value) {
				loadMore.value = true;
			}
		}
	}
};

onMounted(async () => {
	const response = await loadPost($route.query.q);

	const { posts: initialPosts, totalPages: totalPagesNumber } = response;

	totalPages.value = totalPagesNumber;
	posts.value = initialPosts;

	if (process.client) {
		window.scrollTo(0, 0);
		pageTitle.value = $route.query.q;

		store.toggleLoading();

		window.addEventListener("scroll", handleVisibilityChange, {
			passive: true
		});

		const footers = document.querySelectorAll(".footer");
		const disclaimers = document.querySelectorAll(".disclaimer-container");

		if (footers.length > 1) {
			footers[0].remove();
		}
		if (disclaimers.length > 1) {
			disclaimers[0].remove();
		}

		loadMore.value = true;
	}
});

watch(
	() => $route.name,
	(newPath, oldPath) => {
		if (process.client) {
			if (newPath !== oldPath) {
				window.removeEventListener("scroll", handleVisibilityChange);
			}
		}
	}
);

useHead({
	title: `You searched for ${pageTitle.value} - Timothy Sykes`,
	meta: [{ hid: "robots", ...getRobotsMeta(env) }]
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.post-tile {
	border-radius: 8px;
	overflow: hidden;
	filter: drop-shadow(0px 7px 20px rgba(0, 0, 0, 0.1));
}

.post-image {
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	background-color: $primary-300;
	overflow: hidden;
	background-size: cover;
	background-position: center;
}

.post-metadata {
	display: flex;

	span {
		line-height: 1;
		font-family: Rubik;
		font-size: 18x;
		font-style: normal;
		font-weight: 400;
		color: $neutral-400;
	}

	span:first-child {
		padding-right: 8px;
		border-right: 1px solid $neutral-400;
	}

	span:last-child {
		padding-left: 8px;
	}
}

.post-content {
	padding: 8px 16px 16px;
	background-color: white;

	@include breakpoint(md) {
		min-height: 92px;
	}
}

.post-content h3 {
	color: $neutral-800;
	/* Heading/H6 */
	font-family: Fira Sans;
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
	line-height: 120%;
	/* 21.6px */
}
</style>
